import { escapeHtml } from './dom'

/**
 * The viewer's single tabular surface (T3): one renderer for every result set,
 * mounted in the layout shell's bottom zone. WFS features and Data Filter rows
 * differ only by the adapter that feeds this component — same header, same
 * striping, same pagination, same "Show on map" affordance.
 *
 * Only one source is displayed at a time, on purpose (master plan §8.2): the
 * table is opened *per layer*, so showing a second source closes the first.
 * `onDataTableClose` reports the key of whatever stopped being displayed, which
 * is how the layer panel's Attributes toggles stay in sync without any of them
 * knowing about each other.
 */

export interface DataTableColumn {
  key: string
  label: string
  /** Tooltip — used to surface the raw column name behind a friendly label. */
  title?: string
}

export interface DataTableRow {
  cells: Record<string, unknown>
  /** When present, the row gets a "Show" button (zoom/highlight on the map). */
  onShow?: () => void
}

export interface DataTablePage {
  rows: DataTableRow[]
  /** Total across all pages — server-reported for SQL, array length for WFS. */
  total: number
  /** True when `total` is a floor rather than an exact count (server cap). */
  totalIsCapped?: boolean
}

export interface DataTableSort {
  key: string
  dir: 'asc' | 'desc'
}

export interface DataTableSource {
  /** Identifies the owner (a layer name, or `sql`), echoed back by onClose. */
  key: string
  title: string
  /**
   * Read at every render, not once: a WFS source has no columns to offer until
   * its first features have landed, which is *after* the table opens. Adapters
   * that can answer immediately just return a constant.
   */
  columns(): DataTableColumn[]
  pageSize: number
  /**
   * Opt-in: headers become clickable sort controls. Only sources that can order
   * the *whole* result set may set this — sorting one page of a server-paged
   * result would silently lie about what the ordering means. The Data Filter
   * source stays unsorted for exactly that reason (no ORDER BY in the contract).
   */
  sortable?: boolean
  /** Load one 1-based page. Local sources may resolve synchronously. */
  load(page: number, sort?: DataTableSort): DataTablePage | Promise<DataTablePage>
}

const closeListeners: Array<(key: string) => void> = []
let current: DataTableSource | undefined
let currentPage = 1
let currentSort: DataTableSort | undefined
/** Guards against a slow page resolving after the user moved on. */
let loadSeq = 0

/** Notified when a source stops being displayed — closed, or replaced by another. */
export function onDataTableClose(cb: (key: string) => void): void {
  closeListeners.push(cb)
}

function announceClose(key: string): void {
  for (const cb of closeListeners) cb(key)
}

/**
 * The bottom-zone node is pre-created by `layoutShell` so the table lands in
 * the grid; fall back to creating it (tests, or any screen without the shell).
 */
function drawerNode(): HTMLElement {
  let drawer = document.querySelector<HTMLElement>('.attr-drawer')
  if (!drawer) {
    drawer = document.createElement('div')
    drawer.className = 'attr-drawer'
    document.body.appendChild(drawer)
  }
  return drawer
}

/** Open the table on `source`, page 1. Replaces (and closes) any other source. */
export function showDataTable(source: DataTableSource): void {
  const previous = current
  current = source
  currentPage = 1
  currentSort = undefined
  if (previous && previous.key !== source.key) announceClose(previous.key)
  void render()
}

/** Re-read the current page — for live sources whose contents moved on. */
export function refreshDataTable(): void {
  if (current) void render()
}

export function hideDataTable(): void {
  const closed = current
  current = undefined
  loadSeq++
  drawerNode().hidden = true
  if (closed) announceClose(closed.key)
}

export function dataTableKey(): string | undefined {
  return current?.key
}

async function render(): Promise<void> {
  const source = current
  if (!source) return
  const seq = ++loadSeq
  const drawer = drawerNode()
  drawer.hidden = false

  let page: DataTablePage
  try {
    page = await source.load(currentPage, source.sortable ? currentSort : undefined)
  } catch {
    // Adapters own their error reporting (the SQL panel shows proxy errors
    // inline); an unexpected throw here just leaves an empty table.
    page = { rows: [], total: 0 }
  }
  // Another source took over, or the table was closed, while we awaited.
  if (seq !== loadSeq || current !== source) return

  const columns = source.columns()
  const pageCount = Math.max(1, Math.ceil(page.total / source.pageSize))
  const capped = page.totalIsCapped ? '+' : ''
  const summary =
    page.total === 0
      ? ''
      : `${page.total}${capped} row${page.total === 1 ? '' : 's'}${
          pageCount > 1 ? ` · page ${currentPage} of ${pageCount}` : ''
        }`
  const hasShow = page.rows.some((row) => row.onShow)

  // "Show" is the table's most-used affordance (it drives the map), so it leads
  // the row instead of hiding at the end of a horizontally scrolled header.
  const showHead = hasShow ? '<th class="data-table__show-col"></th>' : ''
  const showCell = (row: DataTableRow) =>
    hasShow
      ? `<td class="data-table__show-col">${row.onShow ? `<button type="button" class="data-table__show">Show</button>` : ''}</td>`
      : ''

  const head = (c: DataTableColumn) => {
    const label = escapeHtml(c.label)
    const title = c.title ? ` title="${escapeHtml(c.title)}"` : ''
    if (!source.sortable) return `<th${title}>${label}</th>`
    const active = currentSort?.key === c.key ? currentSort : undefined
    // ▲/▼ when this column drives the order, a dimmed ⇅ otherwise, so every
    // header advertises that it is clickable without shifting width on sort.
    const arrow = active ? (active.dir === 'asc' ? '▲' : '▼') : '⇅'
    const aria = active ? (active.dir === 'asc' ? 'ascending' : 'descending') : 'none'
    return `<th${title} aria-sort="${aria}"><button type="button" class="data-table__sort${
      active ? ' is-active' : ''
    }" data-sort="${escapeHtml(c.key)}">${label}<span class="data-table__arrow">${arrow}</span></button></th>`
  }

  const cell = (value: unknown) => {
    const text = String(value ?? '')
    // Long values (URLs, owner lists) wrap and are clamped to a few lines
    // instead of stretching one column across the whole table; the full value
    // stays reachable in the tooltip.
    return `<td><div class="data-table__cell"${text ? ` title="${escapeHtml(text)}"` : ''}>${escapeHtml(text)}</div></td>`
  }

  // A layer whose every served column is hidden (USA claims, since 2026-09-14)
  // has rows but nothing to put in them: say so rather than draw N rows of a
  // lone "Show" button under an empty header.
  const body =
    page.rows.length === 0
      ? `<p class="attr-drawer__empty">No rows to show.</p>`
      : columns.length === 0
        ? `<p class="attr-drawer__empty">No attributes to show for this layer.</p>`
        : `<div class="attr-drawer__scroll"><table class="data-table">
          <thead><tr>${showHead}${columns.map(head).join('')}</tr></thead>
          <tbody>${page.rows
            .map(
              (row) =>
                `<tr>${showCell(row)}${columns.map((c) => cell(row.cells[c.key])).join('')}</tr>`,
            )
            .join('')}</tbody>
        </table></div>`

  drawer.innerHTML = `
    <div class="attr-drawer__head">
      <strong>${escapeHtml(source.title)}</strong>
      <span class="attr-drawer__count">${summary}</span>
      ${
        pageCount > 1
          ? `<span class="data-table__pager">
              <button type="button" class="btn btn--small" data-page="prev"${currentPage <= 1 ? ' disabled' : ''}>‹ Prev</button>
              <button type="button" class="btn btn--small" data-page="next"${currentPage >= pageCount ? ' disabled' : ''}>Next ›</button>
            </span>`
          : ''
      }
      <button type="button" class="attr-drawer__close" aria-label="Close">×</button>
    </div>
    ${body}`

  drawer.querySelector('.attr-drawer__close')!.addEventListener('click', () => hideDataTable())
  drawer.querySelector('[data-page="prev"]')?.addEventListener('click', () => {
    currentPage--
    void render()
  })
  drawer.querySelector('[data-page="next"]')?.addEventListener('click', () => {
    currentPage++
    void render()
  })
  // asc → desc → unsorted, and back to page 1: the row the user was looking at
  // has no meaning under a new order.
  drawer.querySelectorAll<HTMLButtonElement>('.data-table__sort').forEach((button) => {
    button.addEventListener('click', () => {
      const key = button.dataset.sort!
      currentSort =
        currentSort?.key !== key
          ? { key, dir: 'asc' }
          : currentSort.dir === 'asc'
            ? { key, dir: 'desc' }
            : undefined
      currentPage = 1
      void render()
    })
  })
  const showButtons = drawer.querySelectorAll<HTMLButtonElement>('.data-table__show')
  const showable = page.rows.filter((row) => row.onShow)
  showButtons.forEach((button, i) => {
    button.addEventListener('click', () => showable[i]?.onShow?.())
  })
}
